import { useState } from 'react';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';

import { interpolateTemplate } from 'lib/i18n/dictionary';
import {
  allItems,
  deleteGalleryItemsRequest,
  moveGalleryItemsRequest,
  moveUuid,
  reorderGalleryItemsRequest,
  restoreGalleryItemRequest,
  setGalleryCoverRequest,
  type Gallery,
  type GalleryItem,
  type GallerySection,
} from 'gallery';

import type { GalleryAction } from './gallery-page';
import { ConfirmDialog, sectionLabel, useGalleryText } from './gallery-ui';

interface GalleryGridProps {
  gallery: Gallery;
  // Only this section's items; null for all items in gallery order.
  section: GallerySection | null;
  token?: string;
  run: GalleryAction;
  setGallery: (gallery: Gallery) => void;
}

const DRAG_TYPE = 'application/x-matcha-gallery-item';

// The section's items in the new order, with positions renumbered, for an optimistic reorder.
function withSectionOrder(gallery: Gallery, sectionUuid: string, order: string[]): Gallery {
  return {
    ...gallery,
    sections: gallery.sections.map((section) => {
      if (section.uuid !== sectionUuid) {
        return section;
      }
      const byUuid = new Map(section.items.map((item) => [item.uuid, item]));
      return {
        ...section,
        items: order.map((uuid, position) => ({ ...(byUuid.get(uuid) as GalleryItem), position })),
      };
    }),
  };
}

export function GalleryGrid({ gallery, section, token, run, setGallery }: GalleryGridProps) {
  const { t } = useGalleryText();
  const [dragging, setDragging] = useState<GalleryItem | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; item: GalleryItem } | null>(null);
  const [deleting, setDeleting] = useState<GalleryItem | null>(null);
  const [busy, setBusy] = useState(false);
  const items = section ? section.items : allItems(gallery);
  const picked = new Set(gallery.selection.itemUuids);

  if (!items.length) {
    return (
      <SoftTypography
        variant="button"
        fontWeight="regular"
        color="text"
        py={3}
        data-testid="gallery-grid-empty"
      >
        {section ? t.files.emptySection : t.files.empty}
      </SoftTypography>
    );
  }

  const drop = (target: GalleryItem) => {
    const moving = dragging;
    setDragging(null);
    if (!moving || moving.uuid === target.uuid || moving.sectionUuid !== target.sectionUuid) {
      return;
    }
    const sectionItems =
      gallery.sections.find((entry) => entry.uuid === target.sectionUuid)?.items ?? [];
    const order = moveUuid(
      sectionItems.map((item) => item.uuid),
      moving.uuid,
      target.uuid
    );
    setGallery(withSectionOrder(gallery, target.sectionUuid, order));
    void run(async () => {
      await reorderGalleryItemsRequest(target.sectionUuid, order, token);
    });
  };

  const closeMenu = () => setMenu(null);

  const makeCover = (item: GalleryItem) => {
    closeMenu();
    void run(() => setGalleryCoverRequest(gallery.uuid, item.uuid, token));
  };

  // A Hidden photo comes back for every guest.
  const restore = (item: GalleryItem) => {
    closeMenu();
    void run(() => restoreGalleryItemRequest(item.uuid, token), { success: t.files.restored });
  };

  const moveTo = (item: GalleryItem, sectionUuid: string) => {
    closeMenu();
    void run(async () => {
      await moveGalleryItemsRequest([item.uuid], sectionUuid, token);
    });
  };

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setBusy(true);
    await run(async () => {
      await deleteGalleryItemsRequest([deleting.uuid], token);
    });
    setBusy(false);
    setDeleting(null);
  };

  return (
    <>
      <SoftTypography variant="caption" color="text">
        {t.files.dragHint}
      </SoftTypography>
      <SoftBox
        display="grid"
        gap={1}
        sx={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}
        data-testid="gallery-grid"
      >
        {items.map((item) => {
          const isCover = gallery.coverItemUuid === item.uuid;
          const thumb = item.state === 'READY' ? item.thumb400Url : null;
          return (
            <SoftBox
              key={item.uuid}
              draggable
              onDragStart={(event: React.DragEvent) => {
                event.dataTransfer.setData(DRAG_TYPE, item.uuid);
                event.dataTransfer.effectAllowed = 'move';
                setDragging(item);
              }}
              onDragEnd={() => setDragging(null)}
              onDragOver={(event: React.DragEvent) => {
                if (dragging && dragging.sectionUuid === item.sectionUuid) {
                  event.preventDefault();
                }
              }}
              onDrop={(event: React.DragEvent) => {
                event.preventDefault();
                drop(item);
              }}
              data-testid="gallery-item"
              data-state={item.state}
              data-hidden={item.hidden ? 'true' : undefined}
              sx={{
                position: 'relative',
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'grab',
                aspectRatio: '1',
                bgcolor: 'grey.200',
                outline: isCover ? '3px solid' : 'none',
                outlineColor: 'info.main',
                opacity: dragging?.uuid === item.uuid ? 0.5 : 1,
              }}
            >
              {thumb ? (
                <img
                  src={thumb}
                  srcSet={item.thumb800Url ? `${thumb} 1x, ${item.thumb800Url} 2x` : undefined}
                  alt={item.media.fileName}
                  loading="lazy"
                  draggable={false}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                // PROCESSING (thumbnails being made), FAILED, or no URL: a placeholder with the file name.
                <SoftBox
                  height="100%"
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  p={1}
                  textAlign="center"
                  data-testid="gallery-item-placeholder"
                >
                  {item.state !== 'READY' ? (
                    <SoftTypography variant="caption" fontWeight="bold" color="text">
                      {item.state === 'FAILED' ? t.files.processingFailed : t.files.processing}
                    </SoftTypography>
                  ) : null}
                  <SoftTypography
                    variant="caption"
                    color="text"
                    sx={{
                      width: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.media.fileName}
                  </SoftTypography>
                </SoftBox>
              )}
              {item.hidden ? (
                // Guests no longer see it; dimmed and marked so the Provider knows to bring it back.
                <SoftBox
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  gap={0.5}
                  p={1}
                  textAlign="center"
                  data-testid="gallery-item-hidden"
                  sx={{ color: '#fff', bgcolor: 'rgba(0,0,0,.55)', pointerEvents: 'none' }}
                >
                  <VisibilityOffIcon fontSize="small" />
                  <SoftTypography variant="caption" fontWeight="bold" sx={{ color: '#fff' }}>
                    {t.files.hidden}
                  </SoftTypography>
                </SoftBox>
              ) : null}
              {item.kind === 'VIDEO' ? (
                <SoftBox
                  position="absolute"
                  top={4}
                  left={4}
                  title={t.files.video}
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(0,0,0,.55)',
                    borderRadius: 999,
                    lineHeight: 0,
                    p: 0.25,
                  }}
                >
                  <PlayArrowIcon fontSize="small" />
                </SoftBox>
              ) : null}
              {picked.has(item.uuid) ? (
                <Chip
                  size="small"
                  label="♥"
                  color="error"
                  aria-label={t.counters.selected}
                  sx={{ position: 'absolute', top: 4, right: 36 }}
                />
              ) : null}
              {isCover ? (
                <Chip
                  size="small"
                  label={t.files.cover}
                  color="info"
                  sx={{ position: 'absolute', bottom: 4, left: 4 }}
                  data-testid="gallery-item-cover"
                />
              ) : null}
              <IconButton
                size="small"
                aria-label={t.files.fileActions}
                onClick={(event) => setMenu({ anchor: event.currentTarget, item })}
                data-testid="gallery-item-actions"
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,.55)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,.7)' },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </SoftBox>
          );
        })}
      </SoftBox>
      <Menu anchorEl={menu?.anchor ?? null} open={Boolean(menu)} onClose={closeMenu}>
        {menu?.item.hidden ? (
          <MenuItem onClick={() => restore(menu.item)} data-testid="gallery-item-restore">
            {t.files.restore}
          </MenuItem>
        ) : null}
        {menu && menu.item.kind === 'IMAGE' && gallery.coverItemUuid !== menu.item.uuid ? (
          <MenuItem onClick={() => makeCover(menu.item)} data-testid="gallery-item-make-cover">
            {t.files.makeCover}
          </MenuItem>
        ) : null}
        {menu
          ? gallery.sections
              .filter((entry) => entry.uuid !== menu.item.sectionUuid)
              .map((entry) => (
                <MenuItem
                  key={entry.uuid}
                  onClick={() => moveTo(menu.item, entry.uuid)}
                  data-testid="gallery-item-move"
                >
                  {interpolateTemplate(t.files.moveTo, {
                    name: sectionLabel(entry, t.files.sectionUnnamed),
                  })}
                </MenuItem>
              ))
          : null}
        {menu ? (
          <MenuItem
            onClick={() => {
              setDeleting(menu.item);
              closeMenu();
            }}
            sx={{ color: 'error.main' }}
            data-testid="gallery-item-delete"
          >
            {t.files.deleteFile}
          </MenuItem>
        ) : null}
      </Menu>
      <ConfirmDialog
        open={Boolean(deleting)}
        title={t.files.deleteFileTitle}
        body={interpolateTemplate(t.files.deleteFileBody, { name: deleting?.media.fileName ?? '' })}
        confirmLabel={t.files.deleteFile}
        cancelLabel={t.cancel}
        busy={busy}
        testId="gallery-delete-file-dialog"
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
